import {
  createWalletClient,
  createPublicClient,
  custom,
  http,
  formatEther,
  parseEther,
  formatUnits,
  parseUnits,
  isAddress,
  erc20Abi,
  type Chain,
  type Hex
} from 'viem';

import {
  mainnet,
  base
} from 'viem/chains';

import type { IProvider } from "@web3auth/modal";
import { DEFAULT_ASSET, type AssetConfig } from '@/config/assets';


export default class EthereumRpc {
  private provider: IProvider;

  constructor(provider: IProvider) {
    this.provider = provider;
  }

  private getBaseRpcUrl(): string {
    // Prefer a dedicated RPC for read calls (balance/fees/gas) because some wallet/provider
    // transports can fail with "Internal JSON-RPC error" (e.g. mainnet.base.org 403).
    // Configure VITE_BASE_RPC_URL in Netlify if you have a preferred provider.
    const url = (import.meta.env.VITE_BASE_RPC_URL as string | undefined)?.trim();
    return url && url.length > 0 ? url : 'https://base.publicnode.com';
  }

  private getReadPublicClient() {
    return createPublicClient({
      chain: base,
      transport: http(this.getBaseRpcUrl()),
    });
  }

  getViewChain(): Chain {
    switch (this.provider.chainId) {
      case "8453":
        return base;
      case "0x2105": // Hex for 8453
        return base;
      case "1":
        return mainnet;
      case "0x1":
        return mainnet;
      // case "0x13882":
      //   return polygonAmoy;
      // case "0xaa36a7":
      //   return sepolia;
      default:
        return base; // Default to Base
    }
  }

  async getChainId(): Promise<any> {
    try {
      const walletClient = createWalletClient({
        transport: custom(this.provider)
      })

      await walletClient.getAddresses()

      const chainId = await walletClient.getChainId()
      return chainId.toString();
    } catch (error) {
      return error;
    }
  }

  async getAddresses(): Promise<any> {
    try {
      const walletClient = createWalletClient({
        chain: this.getViewChain(),
        transport: custom(this.provider)
      });

      return await walletClient.getAddresses();
    } catch (error) {
      return error;
    }
  }
  async getAccounts(): Promise<any> {
    try {

      const address = this.getAddresses();

      return address;
    } catch (error) {
      return error;
    }
  }

  /**
   * Get the balance of the specified asset (or DEFAULT_ASSET if none provided).
   * For native ETH, reads on-chain balance. For ERC-20, reads balanceOf on the token contract.
   */
  async getBalance(asset: AssetConfig = DEFAULT_ASSET): Promise<string> {
    try {
      const address = await this.getAccounts();
      const publicClient = this.getReadPublicClient();
      const userAddress = address[0] as `0x${string}`;

      if (asset.contractAddress === null) {
        // Native ETH (future-proofing for potential native currency support)
        const balance = await publicClient.getBalance({ address: userAddress });
        return formatEther(balance);
      }

      // ERC-20 token: read balanceOf from the contract
      const balance = await publicClient.readContract({
        address: asset.contractAddress,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [userAddress],
      }) as bigint;

      return formatUnits(balance, asset.decimals);
    } catch (error) {
      return error as string;
    }
  }

  /**
   * Get the native ETH balance (for gas reserve checks).
   * Always returns ETH balance regardless of the configured asset.
   */
  async getNativeBalance(): Promise<string> {
    try {
      const address = await this.getAccounts();
      const publicClient = this.getReadPublicClient();
      const balance = await publicClient.getBalance({ address: address[0] });
      return formatEther(balance);
    } catch (error) {
      return error as string;
    }
  }

  async switchToBase(): Promise<void> {
    try {
      const currentChainId = await this.provider.request({
        method: "eth_chainId",
      }) as string;
      if (String(currentChainId).toLowerCase() === "0x2105") {
        return;
      }

      await this.provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: "0x2105" }], // Hex for 8453
      });
    } catch (error: any) {
      // If the chain hasn't been added to the wallet yet
      if (error.code === 4902) {
        await this.provider.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: "0x2105",
              chainName: "Base",
              nativeCurrency: {
                name: "Ethereum",
                symbol: "ETH",
                decimals: 18,
              },
              rpcUrls: ["https://mainnet.base.org"],
              blockExplorerUrls: ["https://basescan.org"],
            },
          ],
        });
        return;
      }
      throw error;
    }
  }

  /**
   * Perform a "send max" transfer: send the full balance of the specified asset.
   * For native ETH: sends full balance minus gas reserve.
   * For ERC-20: sends full token balance; precondition is that native ETH covers gas.
   * The `amount` argument is ignored (kept for API compatibility).
   *
   * @param toAddress Recipient address
   * @param _amount Ignored; send-max logic applies regardless
   * @param asset Asset to send (default: DEFAULT_ASSET / USDC)
   * @throws INSUFFICIENT_GAS_ETH if native ETH is insufficient for ERC-20 gas fees
   */
  async sendTransaction(
    toAddress: string,
    _amount: string,
    onSuccess?: (hash: `0x${string}`) => void,
    onError?: (error: any) => void,
    asset: AssetConfig = DEFAULT_ASSET,
  ): Promise<any> {
    try {
      if (!isAddress(toAddress)) {
        throw new Error('Invalid recipient address');
      }

      // First ensure we're on Base network
      await this.switchToBase();

      // Use dedicated public RPC for fee/gas/balance reads.
      const publicClient = this.getReadPublicClient();

      const walletClient = createWalletClient({
        chain: base,
        transport: custom(this.provider)
      });

      const addresses = await this.getAccounts();
      const userAddress = addresses[0] as `0x${string}`;

      const gasPrice = await publicClient.getGasPrice();

      // Prefer viem's EIP-1559 fee estimation if available; fall back to gasPrice heuristics.
      const ONE_GWEI = BigInt(1_000_000_000);
      let maxFeePerGas = gasPrice + ONE_GWEI; // fallback: base + 1 gwei buffer
      let maxPriorityFeePerGas = ONE_GWEI; // fallback: 1 gwei priority fee
      try {
        const fees = await publicClient.estimateFeesPerGas();
        if (fees?.maxFeePerGas) maxFeePerGas = fees.maxFeePerGas;
        if (fees?.maxPriorityFeePerGas) maxPriorityFeePerGas = fees.maxPriorityFeePerGas;
      } catch {
        // ignore, use fallback values
      }

      // Native ETH balance (always needed for gas)
      const nativeBalance = await publicClient.getBalance({ address: userAddress });

      // Fixed buffer (in ETH) to ensure we can always pay gas.
      // Configure via VITE_GAS_BUFFER_ETH if desired; defaults to 0.00004 ETH.
      const bufferEth = (import.meta.env.VITE_GAS_BUFFER_ETH as string | undefined) ?? '0.00004';
      const fixedBufferWei = parseEther(bufferEth);

      if (asset.contractAddress === null) {
        // Native ETH (current behavior, future-proofing)
        const maxGasLimit = BigInt(21000);
        let gasLimit = maxGasLimit;
        try {
          gasLimit = await publicClient.estimateGas({
            account: userAddress,
            to: toAddress as `0x${string}`,
            value: BigInt(1),
          });
        } catch {
          gasLimit = maxGasLimit;
        }

        const estimatedGasWei = maxFeePerGas * gasLimit;
        const gasReserveBigInt = fixedBufferWei > estimatedGasWei ? fixedBufferWei : estimatedGasWei;

        const trySend = async (value: bigint) => {
          try {
            return await walletClient.sendTransaction({
              account: userAddress,
              to: toAddress as `0x${string}`,
              value,
              data: '0x',
              maxFeePerGas,
              maxPriorityFeePerGas,
              gas: gasLimit
            });
          } catch (e: any) {
            if (e?.code === 4001) throw e;
            return await walletClient.sendTransaction({
              account: userAddress,
              to: toAddress as `0x${string}`,
              value,
              data: '0x',
            });
          }
        };

        if (nativeBalance <= gasReserveBigInt) {
          throw new Error(
            `Insufficient funds to cover gas reserve. Balance: ${formatEther(nativeBalance)} ETH, reserve: ${formatEther(gasReserveBigInt)} ETH`
          );
        }

        const sendValue = nativeBalance - gasReserveBigInt;

        try {
          const hash = await trySend(sendValue);
          const receipt = await publicClient.waitForTransactionReceipt({ hash });

          if (onSuccess) {
            onSuccess(hash);
          }

          return this.toObject(receipt);
        } catch (error: any) {
          console.error('Send-max ETH Transaction Parameters:', {
            from: userAddress,
            to: toAddress,
            balance: formatEther(nativeBalance),
            reserve: formatEther(gasReserveBigInt),
            value: formatEther(sendValue),
            maxFeePerGas: maxFeePerGas.toString(),
            maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
            gas: gasLimit.toString()
          });
          console.error('Detailed transaction error:', error);

          if (error.message?.includes('insufficient funds')) {
            throw new Error('Insufficient funds to cover send-max amount and gas fees');
          }
          if (error.message?.includes('gas required exceeds allowance')) {
            throw new Error('Transaction would exceed gas limit');
          }
          if (error.code === 4001) {
            throw new Error('Transaction rejected by user');
          }
          throw error;
        }
      } else {
        // ERC-20 token: send full balance via transfer()
        // Precondition: native ETH must cover estimated gas

        // Read full token balance
        const tokenBalance = await publicClient.readContract({
          address: asset.contractAddress,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [userAddress],
        }) as bigint;

        if (tokenBalance === BigInt(0)) {
          throw new Error(`Zero ${asset.symbol} balance`);
        }

        // Estimate gas for transfer() call (use ~65k fallback for ERC-20)
        let gasLimit = BigInt(65000);
        try {
          gasLimit = await publicClient.estimateContractGas({
            account: userAddress,
            address: asset.contractAddress,
            abi: erc20Abi,
            functionName: 'transfer',
            args: [toAddress as `0x${string}`, tokenBalance],
          });
        } catch {
          gasLimit = BigInt(65000);
        }

        const estimatedGasWei = maxFeePerGas * gasLimit;

        // Reserve enough for gas: use the larger of fixed buffer and estimated gas
        const gasReserveBigInt = fixedBufferWei > estimatedGasWei ? fixedBufferWei : estimatedGasWei;

        // Precondition: wallet must have enough native ETH for gas
        if (nativeBalance < gasReserveBigInt) {
          const err = new Error(
            `Insufficient ETH for gas. Have: ${formatEther(nativeBalance)} ETH, need: ${formatEther(gasReserveBigInt)} ETH`
          );
          (err as any).code = 'INSUFFICIENT_GAS_ETH';
          throw err;
        }

        const trySend = async (amount: bigint) => {
          try {
            return await walletClient.writeContract({
              account: userAddress,
              address: asset.contractAddress!,
              abi: erc20Abi,
              functionName: 'transfer',
              args: [toAddress as `0x${string}`, amount],
              maxFeePerGas,
              maxPriorityFeePerGas,
              gas: gasLimit,
            });
          } catch (e: any) {
            if (e?.code === 4001) throw e;
            // Retry letting the provider/node decide fees
            return await walletClient.writeContract({
              account: userAddress,
              address: asset.contractAddress!,
              abi: erc20Abi,
              functionName: 'transfer',
              args: [toAddress as `0x${string}`, amount],
            });
          }
        };

        try {
          const hash = await trySend(tokenBalance);
          const receipt = await publicClient.waitForTransactionReceipt({ hash });

          if (onSuccess) {
            onSuccess(hash);
          }

          return this.toObject(receipt);
        } catch (error: any) {
          console.error(`Send-max ${asset.symbol} Transaction Parameters:`, {
            from: userAddress,
            to: toAddress,
            tokenBalance: formatUnits(tokenBalance, asset.decimals),
            nativeBalance: formatEther(nativeBalance),
            gasReserve: formatEther(gasReserveBigInt),
            maxFeePerGas: maxFeePerGas.toString(),
            maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
            gas: gasLimit.toString()
          });
          console.error('Detailed transaction error:', error);

          if (error.message?.includes('insufficient funds')) {
            throw new Error(`Insufficient ${asset.symbol} or ETH for transaction and gas fees`);
          }
          if (error.message?.includes('gas required exceeds allowance')) {
            throw new Error('Transaction would exceed gas limit');
          }
          if (error.code === 4001) {
            throw new Error('Transaction rejected by user');
          }
          throw error;
        }
      }

    } catch (error) {
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  /**
   * Send a specific amount of the asset (or DEFAULT_ASSET if none provided).
   * For native ETH: sends the specified ETH amount.
   * For ERC-20: sends the specified token amount; precondition is that native ETH covers gas.
   *
   * @throws INSUFFICIENT_GAS_ETH if native ETH is insufficient for ERC-20 gas fees
   */
  async sendExactTransaction(
    toAddress: string,
    amount: string,
    onSuccess?: (hash: `0x${string}`) => void,
    onError?: (error: any) => void,
    asset: AssetConfig = DEFAULT_ASSET,
  ): Promise<any> {
    try {
      if (!isAddress(toAddress)) {
        throw new Error('Invalid recipient address');
      }

      await this.switchToBase();

      const publicClient = this.getReadPublicClient();
      const walletClient = createWalletClient({
        chain: base,
        transport: custom(this.provider)
      });

      const addresses = await this.getAccounts();
      const account = addresses[0] as `0x${string}`;

      const gasPrice = await publicClient.getGasPrice();
      const ONE_GWEI = BigInt(1_000_000_000);
      let maxFeePerGas = gasPrice + ONE_GWEI;
      let maxPriorityFeePerGas = ONE_GWEI;
      try {
        const fees = await publicClient.estimateFeesPerGas();
        if (fees?.maxFeePerGas) maxFeePerGas = fees.maxFeePerGas;
        if (fees?.maxPriorityFeePerGas) maxPriorityFeePerGas = fees.maxPriorityFeePerGas;
      } catch {
        // ignore and use fallback fee values
      }

      if (asset.contractAddress === null) {
        // Native ETH (current behavior, future-proofing)
        const value = parseEther(amount);
        const balance = await publicClient.getBalance({ address: account });
        if (balance <= value) {
          throw new Error('Insufficient balance for transfer and gas fees');
        }

        let gasLimit = BigInt(21000);
        try {
          gasLimit = await publicClient.estimateGas({
            account,
            to: toAddress as `0x${string}`,
            value,
          });
        } catch {
          gasLimit = BigInt(21000);
        }

        const hash = await walletClient.sendTransaction({
          account,
          to: toAddress as `0x${string}`,
          value,
          data: '0x',
          maxFeePerGas,
          maxPriorityFeePerGas,
          gas: gasLimit,
        });

        const receipt = await publicClient.waitForTransactionReceipt({ hash });
        if (onSuccess) onSuccess(hash);
        return this.toObject(receipt);
      } else {
        // ERC-20 token: send via transfer()
        const tokenAmount = parseUnits(amount, asset.decimals);
        const nativeBalance = await publicClient.getBalance({ address: account });

        // Check token balance
        const tokenBalance = await publicClient.readContract({
          address: asset.contractAddress,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [account],
        }) as bigint;

        if (tokenBalance < tokenAmount) {
          throw new Error(
            `Insufficient ${asset.symbol} balance. Have: ${formatUnits(tokenBalance, asset.decimals)}, need: ${amount}`
          );
        }

        // Estimate gas for transfer() call (use ~65k fallback for ERC-20)
        let gasLimit = BigInt(65000);
        try {
          gasLimit = await publicClient.estimateContractGas({
            account,
            address: asset.contractAddress,
            abi: erc20Abi,
            functionName: 'transfer',
            args: [toAddress as `0x${string}`, tokenAmount],
          });
        } catch {
          gasLimit = BigInt(65000);
        }

        const bufferEth = (import.meta.env.VITE_GAS_BUFFER_ETH as string | undefined) ?? '0.00004';
        const fixedBufferWei = parseEther(bufferEth);
        const estimatedGasWei = maxFeePerGas * gasLimit;
        const gasReserveBigInt = fixedBufferWei > estimatedGasWei ? fixedBufferWei : estimatedGasWei;

        // Precondition: wallet must have enough native ETH for gas
        if (nativeBalance < gasReserveBigInt) {
          const err = new Error(
            `Insufficient ETH for gas. Have: ${formatEther(nativeBalance)} ETH, need: ${formatEther(gasReserveBigInt)} ETH`
          );
          (err as any).code = 'INSUFFICIENT_GAS_ETH';
          throw err;
        }

        const hash = await walletClient.writeContract({
          account,
          address: asset.contractAddress,
          abi: erc20Abi,
          functionName: 'transfer',
          args: [toAddress as `0x${string}`, tokenAmount],
          maxFeePerGas,
          maxPriorityFeePerGas,
          gas: gasLimit,
        });

        const receipt = await publicClient.waitForTransactionReceipt({ hash });
        if (onSuccess) onSuccess(hash);
        return this.toObject(receipt);
      }
    } catch (error) {
      if (onError) onError(error);
      throw error;
    }
  }

  async signMessage(message: string): Promise<Hex> {
    try {
      const walletClient = createWalletClient({
        chain: this.getViewChain(),
        transport: custom(this.provider)
      });

      // data for signing
      const addresses = await this.getAccounts();
      if (!addresses || addresses.length === 0) {
        throw new Error("No accounts found");
      }
      // const originalMessage = "YOUR_MESSAGE";

      // Sign the message
      const signature = await walletClient.signMessage({
        account: addresses[0] as `0x${string}`,
        message: message
      });


      return signature;
    } catch (error) {
      console.error("Error signing message:", error);
      throw error;
    }
  }

  // async readContract() {
  //   try {
  //     const publicClient = createPublicClient({
  //       chain: this.getViewChain(),
  //       transport: custom(this.provider)
  //     })

  //     const number = await publicClient.readContract({
  //       address: "0x9554a5CC8F600F265A89511e5802945f2e8A5F5D",
  //       abi: this.contractABI,
  //       functionName: 'retrieve'
  //     })

  //     return this.toObject(number);
  //   } catch (error) {
  //     return error;
  //   }
  // }

  // async writeContract() {
  //   try {
  //     const publicClient = createPublicClient({
  //       chain: this.getViewChain(),
  //       transport: custom(this.provider)
  //     })

  //     const walletClient = createWalletClient({
  //       chain: this.getViewChain(),
  //       transport: custom(this.provider)
  //     });

  //     // data for writing to the contract
  //     const address = await this.getAccounts();
  //     const randomNumber = Math.floor(Math.random() * 9000) + 1000;

  //     // Submit transaction to the blockchain
  //     const hash = await walletClient.writeContract(
  //       {
  //         account: address[0],
  //         address: "0x9554a5CC8F600F265A89511e5802945f2e8A5F5D",
  //         abi: this.contractABI,
  //         functionName: 'store',
  //         args: [randomNumber]
  //       }
  //     )

  //     const receipt = await publicClient.waitForTransactionReceipt({ hash });


  //     return this.toObject(receipt);
  //   } catch (error) {
  //     return error;
  //   }
  // }

  async generateMtPelerinSignature(): Promise<{ signature: string, code: string, address: string }> {
    try {
      const code = Math.floor(1000 + Math.random() * 9000).toString().padStart(4, '0');
      const message = `MtPelerin-${code}`;
      const signature = await this.signMessage(message);

      // Convert the signature to base64
      const signatureWithoutPrefix = signature.slice(2); // Remove '0x' prefix
      const signatureBuffer = Buffer.from(signatureWithoutPrefix, 'hex');
      const base64Signature = signatureBuffer.toString('base64');

      const addresses = await this.getAccounts();
      if (!addresses || addresses.length === 0) {
        throw new Error("No accounts found");
      }

      return {
        signature: base64Signature,
        code: code,
        address: addresses[0]
      };
    } catch (error) {
      console.error("Error generating Mt Pelerin signature:", error);
      throw error;
    }
  }

  toObject(data: any): any {
    return JSON.parse(JSON.stringify(data, (_, value) =>
      typeof value === 'bigint'
        ? value.toString()
        : value
    ));
  }

}